import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "react-toastify";
import {
  getObjectifsByAdherent,
  createObjectif,
  updateObjectif,
  deleteObjectif,
  setObjectifDone,
  setObjectifProgress,
} from "../../../services/objectifsServices";
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes } from "react-icons/fa";
// ⚠️ Adapte ce service au tien si le nom diffère
import { getAdherentsByUser } from "../../../services/adherentServices";
import CreateEditObjectifModal from "./CreateEditObjectifModal";

/* ---------- Helpers ---------- */

const seasonLabel = (d = new Date()) => {
  const y = d.getFullYear();
  const m = d.getMonth(); // 0..11
  const start = m >= 8 ? y : y - 1; // saison Sep (8) -> Août (7)
  return `${start}-${start + 1}`;
};

const TYPES = [
  "loisir",
  "ceinture",
  "poids",
  "renforcement",
  "competition",
  "kata",
  "uv2",
  "uv3_points",
  "autre",
];

/* ---------- UI atoms ---------- */

function Pill({ children, tone = "violet" }) {
  const map = {
    violet:
      "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200",
    emerald:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200",
    slate:
      "bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-[2px] rounded-full text-xs font-medium ${
        map[tone] || map.violet
      }`}
    >
      {children}
    </span>
  );
}

function Progress({ value = 0 }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="w-full">
      <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
        <div
          className="h-2 rounded-full bg-gradient-to-r from-emerald-500 via-violet-500 to-orange-400 transition-all"
          style={{ width: `${v}%` }}
        />
      </div>
      <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
        {v}%
      </div>
    </div>
  );
}

/* ---------- Carte objectif ---------- */

function ObjectifCard({ o, onEdit, onDelete, onToggleDone, onChangeProgress }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3 sm:p-4 bg-white/80 dark:bg-slate-900/70">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-slate-800 dark:text-slate-100 truncate">
              {o.title}
            </h4>
            {o.saison && <Pill tone="slate">{o.saison}</Pill>}
            {o.type && <Pill tone="emerald">{o.type}</Pill>}
            {o.is_done ? (
              <Pill>Terminé</Pill>
            ) : (
              <Pill tone="slate">En cours</Pill>
            )}
          </div>
          {o.description && (
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              {o.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => onToggleDone(o)}
            className={`px-2 py-2 rounded-lg text-white ${
              o.is_done
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-slate-500 hover:bg-slate-600"
            }`}
            title={o.is_done ? "Marquer non terminé" : "Marquer terminé"}
          >
            <FaCheck />
          </button>
          <button
            onClick={() => onEdit(o)}
            className="px-2 py-2 rounded-lg ring-1 ring-slate-300 dark:ring-slate-700"
            title="Modifier"
          >
            <FaEdit />
          </button>
          <button
            onClick={() => onDelete(o)}
            className="px-2 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white"
            title="Supprimer"
          >
            <FaTrash />
          </button>
        </div>
      </div>

      <div className="mt-3">
        <Progress value={o.progress} />
        {!o.is_done && (
          <div className="mt-2 flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={100}
              value={Number(o.progress) || 0}
              onChange={(e) => onChangeProgress(o, Number(e.target.value))}
              className="w-full"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Composant principal ---------- */

export default function ObjectifsSaisonDisplay({ userId }) {
  const [loading, setLoading] = useState(false);
  const [adherents, setAdherents] = useState([]);
  const [adherentId, setAdherentId] = useState(null);
  const [rows, setRows] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  /* Charger les adhérents liés à l'utilisateur */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) return;
      try {
        const list = await getAdherentsByUser(userId);
        if (cancelled) return;
        const sorted = (list || []).sort(
          (a, b) =>
            (a.nom || "").localeCompare(b.nom || "", "fr", {
              sensitivity: "base",
            }) ||
            (a.prenom || "").localeCompare(b.prenom || "", "fr", {
              sensitivity: "base",
            })
        );
        setAdherents(sorted);
        if (sorted.length === 1) setAdherentId(sorted[0].id);
      } catch (e) {
        toast.error("Impossible de charger vos adhérents.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  /* Charger les objectifs de l’adhérent sélectionné */
  const loadRows = useCallback(async (aid) => {
    if (!aid) return;
    setLoading(true);
    try {
      const data = await getObjectifsByAdherent(aid);
      setRows(data || []);
    } catch (e) {
      toast.error("Erreur lors du chargement des objectifs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adherentId) loadRows(adherentId);
  }, [adherentId, loadRows]);

  const currentAdherent = useMemo(
    () => adherents.find((a) => a.id === Number(adherentId)) || null,
    [adherents, adherentId]
  );

  const openCreate = () => {
    if (!adherentId) return toast.info("Sélectionnez un adhérent.");
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (o) => {
    setEditing(o);
    setModalOpen(true);
  };

  const handleSubmitModal = async (payload, idToUpdate) => {
    try {
      if (!payload.adherent_id) payload.adherent_id = adherentId;
      if (idToUpdate) {
        await updateObjectif(idToUpdate, payload);
        toast.success("Objectif mis à jour.");
      } else {
        await createObjectif(payload);
        toast.success("Objectif créé.");
      }
      setModalOpen(false);
      setEditing(null);
      await loadRows(adherentId);
    } catch (e) {
      toast.error("Échec de l’enregistrement de l’objectif.");
    }
  };

  const handleDelete = async (o) => {
    if (!window.confirm(`Supprimer l’objectif « ${o.title} » ?`)) return;
    try {
      await deleteObjectif(o.id);
      toast.success("Objectif supprimé.");
      await loadRows(adherentId);
    } catch {
      toast.error("Impossible de supprimer cet objectif.");
    }
  };

  const handleToggleDone = async (o) => {
    try {
      await setObjectifDone(o.id, !o.is_done);
      await loadRows(adherentId);
    } catch {
      toast.error("Impossible de changer l’état de l’objectif.");
    }
  };

  const handleChangeProgress = async (o, value) => {
    try {
      await setObjectifProgress(o.id, value);
      // MAJ optimiste
      setRows((r) =>
        r.map((x) =>
          x.id === o.id
            ? {
                ...x,
                progress: value,
                is_done: value >= 100 ? true : x.is_done,
              }
            : x
        )
      );
    } catch {
      toast.error("Échec de la mise à jour de la progression.");
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <label className="block text-sm mb-1">Adhérent</label>
          <select
            className="w-full sm:max-w-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
            value={adherentId || ""}
            onChange={(e) => setAdherentId(Number(e.target.value))}
          >
            <option value="" disabled>
              — Sélectionner —
            </option>
            {adherents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.nom?.toUpperCase?.() || a.nom} {a.prenom}
              </option>
            ))}
          </select>
          {currentAdherent && (
            <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
              Objectifs pour{" "}
              <strong>
                {currentAdherent.nom?.toUpperCase?.()} {currentAdherent.prenom}
              </strong>
            </div>
          )}
        </div>
        <div className="shrink-0">
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-white bg-violet-600 hover:bg-violet-700"
          >
            <FaPlus /> Nouvel objectif
          </button>
        </div>
      </div>

      {/* Liste */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {loading ? (
          <div className="col-span-full text-center text-slate-600 dark:text-slate-300 py-10">
            Chargement…
          </div>
        ) : rows.length === 0 ? (
          <div className="col-span-full text-center text-slate-600 dark:text-slate-300 py-10">
            Aucun objectif pour l’instant.
          </div>
        ) : (
          rows.map((o) => (
            <ObjectifCard
              key={o.id}
              o={o}
              onEdit={openEdit}
              onDelete={handleDelete}
              onToggleDone={handleToggleDone}
              onChangeProgress={handleChangeProgress}
            />
          ))
        )}
      </div>

      <CreateEditObjectifModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmitModal}
        initial={editing}
        adherentId={adherentId}
      />
    </div>
  );
}
